import { z } from "zod";

/**
 * Update Deployment Path Variables Schema
 */
export const UpdateDeploymentPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * Update Deployment Body Schema
 *
 * All fields are optional - only send what needs to be updated.
 *
 * WhatsApp reassignment: prefer `whatsappSenderId` over `phoneNumberId`. The
 * backend resolves the sender to its current `phoneNumberId` + `apiKeyConnectionId`
 * pair and, if a different active WHATSAPP deployment already owns that sender,
 * returns 409 unless `force: true` was sent. With `force`, the previous owner
 * is detached and disabled in a single transaction before the new one attaches.
 */
export const UpdateDeploymentBodySchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  description: z.string().max(1000).nullable().optional(),
  agentId: z.string().uuid().optional(),
  /**
   * The settings object, unnarrowed — `deploymentSettings` has no shape
   * independent of the deployment's TYPE, and the type is on the row rather
   * than in this body, so no schema here can know which one to apply.
   *
   * `UpdateDeploymentUseCase` reads the row, validates this against that type's
   * own schema, and writes one `jsonb_set` per top-level group the request
   * named. A group the request omits is not in the statement at all.
   *
   * 🔑 AN UNDECLARED KEY SURVIVES THAT WRITE, AND THAT IS THE DECIDED CONTRACT
   * RATHER THAN AN OVERSIGHT. `deploymentSettings` is closed at a BIRTH and
   * open on an UPDATE: stripping here would delete stored keys, and 162 of 432
   * live deployments already fail their own type's schema, while nine of the
   * twenty-two types map to a bare `z.record` with no declared shape to close
   * against. `settings-not-carried-to-a-copy.ts` carries the argument in full.
   *
   * debt: this slot accepts any JSON object, so a caller's typo is persisted
   *       under the group it named instead of being refused.
   *       Ceiling: the per-type schema still gates VALIDITY at the use case, so
   *       a body that breaks a group is refused; what is not refused is an
   *       extra key inside a group that still parses.
   *       Upgrade trigger: a re-run of the STORED-ROW census — undeclared keys
   *       per group across live deployments — whose prior reading is the 162 of
   *       432 above. The rows ARE the accumulated record of what callers sent, so
   *       that census is retrospective and needs no telemetry. Below a rate worth
   *       a migration, narrow this slot to a discriminated union over `type`
   *       resolved at the use case; at or above it, the keys are load-bearing for
   *       somebody and narrowing is a breaking change that needs its own
   *       deprecation.
   *       NOT `v1_discarded_body_keys_total{route}`, which is the attractive wrong
   *       instrument here because its NAME fits and it is already shipped. It
   *       cannot observe this ceiling: `discardedTopLevelKeys` compares
   *       `Object.keys` ONE level deep, and `deploymentSettings` is declared AT
   *       that level with a slot accepting every nested key — so nothing inside a
   *       group is ever discarded and that series stays flat however many typos
   *       land. It counts stray TOP-LEVEL keys, which is exactly right for the 140
   *       lenient v1 body schemas it was built for and a different population from
   *       this one.
   */
  deploymentSettings: z.record(z.string(), z.unknown()).optional(),
  phoneNumberId: z.string().uuid().nullable().optional(),
  // NOT a UUID: WhatsappSender.id is the Twilio sender SID (e.g. "XE…").
  whatsappSenderId: z.string().min(1).optional(),
  oauthConnectionId: z.string().uuid().nullable().optional(),
  apiKeyConnectionId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
  force: z.boolean().optional()
});

export type UpdateDeploymentPathVars = z.infer<typeof UpdateDeploymentPathVarsSchema>;
export type UpdateDeploymentBody = z.infer<typeof UpdateDeploymentBodySchema>;
